// AssessmentDashboard.jsx - Enhanced dashboard with Foundations analytics
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart3, Building, Shield, Database, Users,
  Download, FileText, TrendingUp, AlertTriangle, CheckCircle,
  Calendar, User, Settings, RefreshCw, Plus, Eye, ArrowLeft,
  Award, Activity, Clock, Target, ExternalLink, ChevronRight, ChevronDown,
  Zap, TrendingDown, History, LogOut, PieChart, Layers,
  BarChart, LineChart, Gauge, Filter, Search, Bell, DollarSign,
  Briefcase, Code, ShieldCheck, UserCheck, Upload,
  ArrowUpCircle, XCircle, EyeOff
} from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge, RiskBadge, StatusBadge, PriorityBadge } from '../../../components/ui/Badge';
import apiConfig from '../../../config/apiConfig';
import Dartboard360 from '../../compass/components/boardreports/Dartboard360';
import RiskInsightPanel from '../components/RiskInsightPanel';
import CorporatePosturePanel from '../components/CorporatePosturePanel';
import apiService from '../../../services/api';
import { authHeaders } from '../../../services/authHeaders';
import IrisLoadingOverlay from '../../../shared/components/IrisLoadingOverlay';
import RecommendationGroundingPanel, { GroundedStepLine } from '../../defense/components/RecommendationGroundingPanel';
import HelpTooltip from '../../../shared/components/HelpTooltip';
import HowItWorksGuide from '../../../shared/components/HowItWorksGuide';
// DivergenceModal removed — divergence renders inline
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export const SESSION_NOT_ON_ACCOUNT =
  "This assessment isn't available to the account you're signed in as. If it belongs to another account, sign out and sign in with that one.";

const AssessmentDashboard = ({ companyId: propCompanyId, onBack }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(propCompanyId);
  const loadingRef = useRef(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [analytics360, setAnalytics360] = useState(null);
  const [weightingFactors, setWeightingFactors] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [outsidePressures, setOutsidePressures] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionNotFound, setSessionNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [prismContext, setPrismContext] = useState(null);
  const [prismSignals, setPrismSignals] = useState(null); // Sprint 2: SPRS, ATO Journey, drift
  const [exporting, setExporting] = useState(false);
  const [divergenceOpen, setDivergenceOpen] = useState(false);
  const [divergenceData, setDivergenceData] = useState(null);
  const [divergenceLoading, setDivergenceLoading] = useState(false);
  const dashboardRef = useRef(null);

  const handleOpenDivergence = async () => {
    if (divergenceData) {
      setDivergenceOpen(!divergenceOpen);
      return;
    }
    setDivergenceLoading(true);
    try {
      const url = `${apiConfig.getApiBaseUrl()}/assessments/sessions/${sessionId}/divergence`;
      const response = await fetch(url, { headers: authHeaders() });
      const result = await response.json();
      if (result.success && result.data) {
        setDivergenceData(result.data);
        setDivergenceOpen(true);
      }
    } catch (err) {
      console.error('[Divergence] Failed to load:', err);
    } finally {
      setDivergenceLoading(false);
    }
  };

  const handleExportReport = async () => {
    if (!dashboardRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#0f172a',
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let y = 10;
      let remainingHeight = imgHeight;

      // Multi-page support for long dashboards
      while (remainingHeight > 0) {
        if (y > 10) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, y - (imgHeight - remainingHeight), imgWidth, imgHeight);
        remainingHeight -= (pageHeight - 20);
        y = 10;
      }

      const companyName = dashboardData?.company?.name || 'Assessment';
      const date = new Date().toISOString().split('T')[0];
      pdf.save(`${companyName}_Risk_Report_${date}.pdf`);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('currentSessionId', sessionId);
      localStorage.setItem('activeSessionId', sessionId);
      loadSessionAndDashboardData();
    } else if (companyId && !sessionId) {
      loadDashboardData();
    }
  }, [sessionId, companyId]);

  const loadSessionAndDashboardData = async () => {
    
    try {
      setLoading(true);
      setError(null);
      setSessionNotFound(false);

      // Get session info to determine company
      const sessionResponse = await fetch(`${apiConfig.getApiBaseUrl()}/foundations/session/${sessionId}/info`, { headers: authHeaders() });
      
      // 404 = not this account's session (ownership answers "not yours" and "does not exist" alike).
      // Say so, and stop storing the id so the sidebar's Results link does not keep leading here.
      if (sessionResponse.status === 404) {
        for (const key of ['activeSessionId', 'currentSessionId']) {
          if (localStorage.getItem(key) === String(sessionId)) localStorage.removeItem(key);
        }
        setSessionNotFound(true);
        throw new Error(SESSION_NOT_ON_ACCOUNT);
      }
      if (!sessionResponse.ok) {
        throw new Error('Failed to load session information');
      }
      
      const sessionData = await sessionResponse.json();
      
      if (!sessionData.success) {
        throw new Error(sessionData.error || 'Failed to load session information');
      }
      
      setSessionInfo(sessionData.session);
      setCompanyId(sessionData.session.companyName);
      await loadDashboardData(sessionData.session.companyName, sessionId);
      
    } catch (error) {
      console.error('❌ Error loading session and dashboard data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const loadDashboardData = async (targetCompanyId = companyId, targetSessionId = sessionId) => {
    
    try {
      setLoading(true);
      setError(null);
      
      
      // Load data sources sequentially to avoid overwhelming backend
      let dashboardResponse = { status: 'rejected', reason: new Error('Not loaded') };
      let analytics360Response = { status: 'rejected', reason: new Error('Not loaded') };
      let weightingResponse = { status: 'rejected', reason: new Error('Not loaded') };
      // recommendationsResponse handled asynchronously (Iris AI)
      let outsidePressuresResponse = { status: 'rejected', reason: new Error('Not loaded') };
      
      try {
        // Load 360 Analytics first
        analytics360Response = { status: 'fulfilled', value: await apiService.get360Analytics(targetSessionId) };
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Load Recommendations asynchronously (Iris AI may take 10+ seconds)
        setRecommendationsLoading(true);
        apiService.getRecommendations(targetSessionId)
          .then(value => {
            const rawRecommendations = value.data;
            if (rawRecommendations && rawRecommendations.recommendations) {
              const transformedRecommendations = {
                ...rawRecommendations,
                recommendations: rawRecommendations.recommendations.map(rec => {
                  if (rec.text && rec.metadata) {
                    return {
                      title: rec.text.split('\n')[0] || 'Recommendation',
                      recommendation: rec.text,
                      category: rec.metadata.category || 'General',
                      type: rec.metadata.rec_type || 'Recommendation',
                      effort: rec.metadata.effort_score || 'Medium',
                      impact: rec.metadata.impact_score || 'Medium',
                      cost: rec.metadata.estimated_cost || '$$',
                      timeline: rec.metadata.typical_timeline || '1-3 months',
                      reason: 'AI-selected based on risk assessment',
                      actionSteps: [],
                      aiInsights: null,
                      ...rec.metadata
                    };
                  }
                  return rec;
                })
              };
              setRecommendations(transformedRecommendations);
            } else {
              setRecommendations(rawRecommendations);
            }
          })
          .catch(err => console.warn('Failed to load recommendations:', err))
          .finally(() => setRecommendationsLoading(false));
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Load Outside Pressures
        outsidePressuresResponse = { status: 'fulfilled', value: await apiService.getOutsidePressures(targetSessionId) };
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Load Weighting Factors
        weightingResponse = { status: 'fulfilled', value: await apiService.getWeightingFactors(targetSessionId) };
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Load Dashboard Data
        dashboardResponse = { status: 'fulfilled', value: await apiService.getDashboardData(targetCompanyId, targetSessionId) };
        
      } catch (error) {
        console.error('Error loading API data:', error);
      }
      

      // Handle dashboard data
      if (dashboardResponse.status === 'fulfilled') {
        // Business Intelligence API returns data directly
        const biData = dashboardResponse.value;
        const transformedDashboardData = {
          company: {
            name: biData.companyName || 'Unknown Company'
          },
          overallRiskScore: {
            score: biData.riskScore || 0,
            riskLevel: biData.riskScore >= 70 ? 'High' : biData.riskScore >= 40 ? 'Medium' : 'Low'
          },
          enhancedCategories: biData.enhancedCategories || {},
          big6Scores: biData.categoryScores || {},
          industry: biData.industry || 'General',
          revenueAtRisk: biData.revenueAtRisk,
          revenueAtRiskRaw: biData.revenueAtRiskRaw,
          recoveryDays: biData.recoveryDays,
          investmentScenarios: biData.investmentScenarios || []
        };
        setDashboardData(transformedDashboardData);
      } else {
        console.warn('Failed to load dashboard data:', dashboardResponse.reason);
      }

      // Handle 360 analytics
      if (analytics360Response.status === 'fulfilled') {
        setAnalytics360(analytics360Response.value);
      } else {
        console.warn('Failed to load 360 analytics:', analytics360Response.reason);
      }

      // Handle weighting factors
      if (weightingResponse.status === 'fulfilled') {
        setWeightingFactors(weightingResponse.value.data);
      } else {
        console.warn('Failed to load weighting factors:', weightingResponse.reason);
      }

      // Recommendations handled asynchronously above (Iris AI loading)

      // Load PRISM context for recovery timeline enrichment (non-blocking)
      try {
        const prismRes = await fetch(apiConfig.foundations.getPrismData(sessionId), {
          headers: authHeaders(),
        });
        const prismJson = await prismRes.json();
        if (prismJson.success && prismJson.data?.connected !== false) {
          setPrismContext(prismJson.data);
        }
      } catch {
        // PRISM unavailable — no enrichment
      }

      // Sprint 2: Load PRISM signals (SPRS, ATO Journey, drift) non-blocking
      try {
        const res = await fetch(`${apiConfig.getApiBaseUrl()}/foundations/prism-signals/${sessionId}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (data?.success) {
          setPrismSignals(data);
        }
      } catch {
        // Non-fatal — results page still renders
      }

      // Handle outside pressures
      if (outsidePressuresResponse && outsidePressuresResponse.status === 'fulfilled') {
        if (outsidePressuresResponse.value.success) {
          // Set the entire response object to match the UI expectations
          setOutsidePressures(outsidePressuresResponse.value);
        }
      } else if (outsidePressuresResponse) {
        console.warn('Failed to load outside pressures:', outsidePressuresResponse.reason);
      }
      
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const refreshData = () => {
    loadDashboardData();
  };

  // Error BEFORE the spinner: a failed session load leaves sessionInfo null, so the spinner branch
  // below would otherwise render forever and the error would reach only the console.
  if (error && !loading) {
    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)] flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <Card.Body className="text-center">
            <AlertTriangle aria-hidden="true" className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#e6f1ff] mb-2">
              {sessionNotFound ? 'Assessment Not Available' : 'Error Loading Dashboard'}
            </h3>
            <p className="text-[#a8b2d1] mb-4">{error}</p>
            {sessionNotFound ? (
              <Button onClick={() => navigate('/coordinator-dashboard')} variant="primary">
                Go to Your Dashboard
              </Button>
            ) : (
              <Button onClick={refreshData} variant="primary">
                Try Again
              </Button>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (loading || !sessionInfo) {
    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-[#a8b2d1]">Loading Foundations Analytics...</p>
        </div>

      </div>
    );
  }

  const tabConfig = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 aria-hidden="true" className="w-4 h-4" /> },
    { id: 'recommendations', label: 'Recommendations', icon: <TrendingUp aria-hidden="true" className="w-4 h-4" /> },
    { id: 'policy', label: 'Evidence Intelligence', icon: <Shield aria-hidden="true" className="w-4 h-4" /> }
  ];


  return (
    <div className="min-h-screen bg-[rgba(255,255,255,0.03)]">
      {/* Header */}
      <div className="ra-glass border-b border-[rgba(255,255,255,0.12)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={onBack || (() => navigate(-1))}
                icon={<ArrowLeft aria-hidden="true" className="w-4 h-4" />}
              >
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-[#e6f1ff] flex items-center gap-2">
                  Foundations Risk Assessment <HelpTooltip contentKey="foundations.assessmentDashboard" iconSize="md" />
                </h1>
                <p className="text-[#a8b2d1]">
                  {dashboardData?.company?.name || 'Company Assessment'} • Enhanced Analytics
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                onClick={handleOpenDivergence}
                disabled={divergenceLoading}
                icon={<Activity aria-hidden="true" className="w-4 h-4" />}
              >
                {divergenceLoading ? 'Loading...' : 'Persona Divergence'}
              </Button>
              <Button
                variant="ghost"
                onClick={refreshData}
                icon={<RefreshCw aria-hidden="true" className="w-4 h-4" />}
              >
                Refresh
              </Button>
              <Button
                variant="primary"
                onClick={handleExportReport}
                disabled={exporting}
                icon={<Download aria-hidden="true" className="w-4 h-4" />}
              >
                {exporting ? 'Exporting...' : 'Export Report'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Guide */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <HowItWorksGuide
          moduleName="the Assessment Dashboard"
          steps={[
            { title: 'Cyber Risk Score', desc: 'Your CRS is the single-number measure of organizational cyber risk. It combines all persona responses, Big 6 categories, and weighting factors into a 0-100 score with industry benchmarking.' },
            { title: 'Big 6 Categories', desc: 'Six risk domains — Governance, Technology, Data, People, Operations, and Third-Party — each scored independently. Drill into any category to see which questions drove the score and where personas disagreed.' },
            { title: 'Persona Comparison', desc: 'See how each persona (CEO, CISO, CFO, HR, Legal) answered. Divergence between personas reveals organizational blind spots — where leadership perception differs from operational reality.' },
            { title: 'Business Intelligence', desc: 'Navigate to Business Intelligence for outside pressures analysis, board-ready reports, and investment optimization recommendations based on your assessment results.' },
            { title: 'Export & Share', desc: 'Generate a PDF report for stakeholders, board presentations, or audit documentation. Your assessment data also flows to PRISM (if connected) for technical deep-dive against your organizational risk baseline.' },
          ]}
          footnote="Scores update in real time as persona responses are submitted. Use the Export Report button to generate a PDF for stakeholders."
        />
      </div>

      {/* Persona Divergence Panel (inline) */}
      {divergenceOpen && divergenceData && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 12,
            overflow: 'hidden',
          }}>
            {/* Summary Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', gap: 24,
            }}>
              <div style={{ textAlign: 'center', minWidth: 80 }}>
                <div style={{
                  fontSize: '2.2rem', fontWeight: 800,
                  color: divergenceData.summary.agreement_pct >= 80 ? '#34d399' : divergenceData.summary.agreement_pct >= 60 ? '#fbbf24' : '#f87171',
                  lineHeight: 1,
                }}>
                  {divergenceData.summary.agreement_pct}%
                </div>
                <div style={{ color: '#7a8599', fontSize: '0.7rem', marginTop: 4 }}>Agreement</div>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ color: '#e6f1ff', fontSize: '1.1rem', fontWeight: 700, margin: '0 0 4px' }}>
                  Persona Divergence Analysis
                </h3>
                <p style={{ color: '#a8b2cc', fontSize: '0.85rem', margin: 0 }}>
                  {divergenceData.summary.personas_responded.join(', ')} agreed on <strong>{divergenceData.summary.agreement_count} of {divergenceData.summary.total_questions}</strong> questions
                  {divergenceData.summary.divergent_count > 0 && (
                    <> but <span style={{ color: '#f87171', fontWeight: 600 }}>diverged on {divergenceData.summary.divergent_count}</span></>
                  )}
                </p>
              </div>
              <button
                onClick={() => setDivergenceOpen(false)}
                style={{ background: 'none', border: 'none', color: '#7a8599', fontSize: '1.2rem', cursor: 'pointer', padding: '4px 8px' }}
              >&#10005;</button>
            </div>

            {/* Divergent Questions */}
            {divergenceData.divergent_questions.length > 0 && (
              <div style={{ padding: '16px 24px' }}>
                <h4 style={{ color: '#f87171', fontSize: '0.85rem', fontWeight: 600, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f87171', display: 'inline-block' }} />
                  Disagreements ({divergenceData.divergent_questions.length})
                </h4>
                {divergenceData.divergent_questions.map((q, idx) => (
                  <div key={idx} style={{
                    padding: '12px 16px', marginBottom: 8,
                    background: 'rgba(248, 113, 113, 0.04)',
                    border: '1px solid rgba(248, 113, 113, 0.12)',
                    borderRadius: 8,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <p style={{ color: '#e6f1ff', fontSize: '0.85rem', margin: 0, lineHeight: 1.4, flex: 1, paddingRight: 12 }}>
                        {q.question_text}
                      </p>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700,
                        background: 'rgba(248,113,113,0.15)', color: '#f87171', flexShrink: 0,
                      }}>
                        Spread: {q.spread}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {q.scores.map((s, i) => {
                        const diff = Math.abs(s.score - parseFloat(q.avg_score));
                        const color = diff <= 0.5 ? '#34d399' : diff <= 1.5 ? '#fbbf24' : '#f87171';
                        return (
                          <span key={i} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 12,
                            background: `${color}15`, border: `1px solid ${color}30`,
                            fontSize: '0.75rem', fontWeight: 600, color,
                          }}>
                            <span style={{ color: '#a8b2cc', fontWeight: 400, fontSize: '0.7rem' }}>{s.persona}:</span>
                            {s.score}
                          </span>
                        );
                      })}
                      <span style={{ fontSize: '0.7rem', color: '#5a6577', alignSelf: 'center' }}>avg: {q.avg_score}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sprint 2 — PRISM drift banner (SPRS + ATO now in PRISM Posture card) */}
      {prismSignals?.linked && prismSignals?.drift?.open_count > 0 && (
        <PrismDriftBanner drift={prismSignals.drift} />
      )}

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="ra-glass border-b border-[rgba(255,255,255,0.12)]">
          <nav className="flex space-x-8">
            {tabConfig.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-[#8892b0] hover:text-[#ccd6f6] hover:border-[rgba(255,255,255,0.18)]'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div ref={dashboardRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'overview' && (
          <OverviewTab
            dashboardData={dashboardData}
            analytics360={analytics360}
            weightingFactors={weightingFactors}
            sessionId={sessionId}
            prismContext={prismContext}
            prismSignals={prismSignals}
          />
        )}
        
        {activeTab === 'recommendations' && (
          recommendationsLoading
            ? <IrisLoadingOverlay visible={true} />
            : <RecommendationsTab recommendations={recommendations} />
        )}
        
        {activeTab === 'policy' && (
          <PolicyIntegrationTab dashboardData={dashboardData} />
        )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ dashboardData, analytics360, weightingFactors, sessionId, prismContext, prismSignals }) => {
  // Extract enhanced categories from dashboard data (these are our Dynamic Category Scores)
  const enhancedCategories = dashboardData?.enhancedCategories || {};
  
  return (
    <div className="space-y-6">
      {/* Risk Score Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <Card.Body>
            <p className="text-sm font-medium text-[#a8b2d1] mb-3 flex items-center gap-1.5">Optimization Gaps <HelpTooltip contentKey="foundations.big6Bars" side="right" /></p>
            {(() => {
              const big6 = dashboardData?.big6Scores || {};
              const industry = dashboardData?.industry || 'General';
              const labels = {
                governance: 'Governance',
                technicalDebt: 'Technical Debt',
                culture: 'Culture',
                thirdParty: 'Third Party',
                budget: 'Budget',
                people: 'People',
              };
              // Industry-specific Goldilocks bands [bestInClass, standard]
              const industryProfiles = {
                Finance:       { governance: [75, 90], culture: [78, 90], budget: [82, 95], technicalDebt: [85, 95], thirdParty: [72, 88], people: [78, 92] },
                Healthcare:    { governance: [72, 88], culture: [75, 88], budget: [80, 92], technicalDebt: [82, 92], thirdParty: [70, 85], people: [75, 90] },
                Technology:    { governance: [70, 85], culture: [75, 90], budget: [78, 92], technicalDebt: [85, 98], thirdParty: [68, 82], people: [72, 88] },
                Manufacturing: { governance: [70, 85], culture: [75, 90], budget: [80, 95], technicalDebt: [85, 100], thirdParty: [65, 80], people: [72, 88] },
                Energy:        { governance: [72, 87], culture: [74, 88], budget: [80, 93], technicalDebt: [83, 95], thirdParty: [68, 83], people: [73, 88] },
                Retail:        { governance: [62, 80], culture: [65, 82], budget: [72, 88], technicalDebt: [78, 90], thirdParty: [60, 78], people: [65, 82] },
                Education:     { governance: [60, 78], culture: [62, 80], budget: [68, 85], technicalDebt: [75, 88], thirdParty: [58, 75], people: [62, 80] },
                'Real Estate': { governance: [58, 76], culture: [60, 78], budget: [66, 84], technicalDebt: [72, 86], thirdParty: [56, 74], people: [60, 78] },
                Transportation:{ governance: [65, 82], culture: [68, 84], budget: [75, 90], technicalDebt: [80, 92], thirdParty: [62, 80], people: [68, 85] },
              };
              const defaultBands = { governance: [65, 82], culture: [68, 85], budget: [75, 90], technicalDebt: [80, 92], thirdParty: [62, 78], people: [68, 85] };
              const bands = industryProfiles[industry] || defaultBands;

              const entries = Object.entries(labels).map(([key, label]) => {
                const score = big6[key] ?? null;
                const [lo, hi] = bands[key] || [65, 85];
                const s = Math.min(Math.max(score ?? 0, 0), 100);
                // Gap = distance from nearest edge of optimal band. 0 = in the zone.
                const gap = s < lo ? lo - s : s > hi ? s - hi : 0;
                return { key, label, score, gap, inBand: gap === 0 };
              });
              const hasData = entries.some(e => e.score !== null);
              if (!hasData) return <p className="text-[#8892b0] text-sm">Complete assessment to see optimization gaps</p>;

              // Max possible gap for bar scaling (use largest actual gap, floor 30)
              const maxGap = Math.max(30, ...entries.map(e => e.gap));

              return (
                <div className="space-y-2">
                  {entries.map(({ key, label, gap, inBand }) => {
                    const pct = inBand ? 0 : Math.max(4, (gap / maxGap) * 100);
                    const color = gap <= 10 ? '#eab308' : '#ef4444';
                    return (
                      <div key={key}>
                        <div className="flex justify-between text-xs mb-0.5">
                          <span className="text-[#8892b0]">{label}</span>
                          {inBand && <span style={{ color: '#22c55e', fontSize: '0.65rem', fontWeight: 600 }}>Optimized</span>}
                        </div>
                        <div className="w-full h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                          {!inBand && (
                            <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#a8b2d1] flex items-center gap-1.5">Security Investment ROI <HelpTooltip contentKey="foundations.investmentROI" /></p>
                <p className="text-3xl font-bold text-[#e6f1ff]">
                  {(() => {
                    if (!dashboardData?.investmentScenarios) return 'Loading...';
                    const underInvestment = dashboardData.investmentScenarios.find(s => s.label === 'Under-Investment');
                    const currentInvestment = dashboardData.investmentScenarios.find(s => s.isCurrent);
                    if (underInvestment && currentInvestment) {
                      const riskReduction = underInvestment.risk - currentInvestment.risk;
                      const roi = riskReduction / currentInvestment.investment;
                      return `${roi.toFixed(1)}x`;
                    }
                    return '1.4x';
                  })()}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign aria-hidden="true" className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <Badge variant="success">
                High Return Investment
              </Badge>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#a8b2d1] flex items-center gap-1.5">Revenue at Risk <HelpTooltip contentKey="foundations.revenueAtRiskCard" /></p>
                <p className="text-2xl font-bold text-[#e6f1ff]">
                  {dashboardData?.revenueAtRisk || 'Loading...'}
                </p>
                <p className="text-sm text-[#a8b2d1] mt-1">
                  Recovery: {dashboardData?.recoveryDays ? `${dashboardData.recoveryDays} days` : 'Calculating...'}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle aria-hidden="true" className="h-6 w-6 text-red-600" />
              </div>
            </div>
            {prismContext?.pdr_maturity?.respond != null ? (
              <div className="mt-3 px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                <span className="text-blue-400 font-semibold">PRISM Respond Maturity: {Number(prismContext.pdr_maturity.respond).toFixed(1)}/4.0</span>
                {Number(prismContext.pdr_maturity.respond) < 2.0 && (
                  <span className="text-[#8892b0] ml-1">— recovery estimate may need adjustment</span>
                )}
              </div>
            ) : (
              <div className="mt-3 px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="text-[#8892b0]">Connect PRISM for validated recovery estimates based on actual IR capabilities</span>
              </div>
            )}
            <div className="mt-3">
              <Badge variant="danger">
                Critical Business Impact
              </Badge>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* PRISM Corporate Security Posture roll-up — promoted above 360 cards.
          Replaced the single-project PrismInsightBanner per Roger 2026-06-04. */}
      <CorporatePosturePanel
        sessionId={sessionId}
        companyName={dashboardData?.company?.name || dashboardData?.companyName || dashboardData?.company_name}
        prismSignals={prismSignals}
      />

      {/* 360° Risk Assessment Analysis */}
      {analytics360 && analytics360.data && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 aria-hidden="true" className="w-5 h-5 text-[#a8b2d1]" />
            <h2 className="text-xl font-semibold text-[#e6f1ff]">360° Risk Assessment Analysis</h2>
          </div>

          <RiskInsightPanel analytics360={analytics360} sessionId={sessionId} />
        </div>
      )}

      {/* Category Risk Assessment - TEMPORARILY DISABLED
          The Big 6 categories are still calculated in the backend but not displayed here
          This section can be re-enabled once proper weighting calculations are implemented
      
      {Object.keys(enhancedCategories).length > 0 && (
        <Card>
          <Card.Header>
            <Card.Title icon={<BarChart3 />}>Category Risk Assessment</Card.Title>
            <Card.Description>
              Risk scores across six key organizational categories
            </Card.Description>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(enhancedCategories).map(([categoryId, categoryData]) => {
                const score = categoryData.score || 0;
                const name = categoryData.name || categoryId;
                const riskLevel = categoryData.riskLevel || 'Unknown';
                const factors = categoryData.factors || 0;

                // Get appropriate icon for each category
                const getCategoryIcon = (categoryId) => {
                  switch (categoryId) {
                    case 'governance': return <Shield aria-hidden="true" className="w-5 h-5" />;
                    case 'techDebt': return <Settings aria-hidden="true" className="w-5 h-5" />;
                    case 'culture': return <Users aria-hidden="true" className="w-5 h-5" />;
                    case 'thirdParty': return <Globe aria-hidden="true" className="w-5 h-5" />;
                    case 'budget': return <TrendingUp aria-hidden="true" className="w-5 h-5" />;
                    case 'people': return <User aria-hidden="true" className="w-5 h-5" />;
                    default: return <AlertTriangle aria-hidden="true" className="w-5 h-5" />;
                  }
                };

                return (
                  <div 
                    key={categoryId} 
                    className="ra-glass border border-[rgba(255,255,255,0.12)] rounded-lg p-4 hover:shadow-[0_4px_24px_rgba(0,0,0,0.3)] transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className={`p-2 rounded-lg ${
                          score <= 2 ? 'bg-green-100 text-green-600' :
                          score <= 4 ? 'bg-yellow-100 text-yellow-600' :
                          score <= 6 ? 'bg-orange-100 text-orange-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {getCategoryIcon(categoryId)}
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#e6f1ff]">{name}</h4>
                          <p className="text-xs text-[#8892b0]">{factors} factors</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#e6f1ff]">{score}</p>
                        <RiskBadge level={riskLevel.toLowerCase()} size="sm" />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="w-full bg-[#1e3148] rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-500 ${
                            score <= 2 ? 'bg-green-500' : 
                            score <= 4 ? 'bg-yellow-500' : 
                            score <= 6 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min((score / 10) * 100, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-[#8892b0]">
                        <span>0</span>
                        <span>10</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}
      */}

    </div>
  );
};


// Enhanced Recommendations Tab Component - RAG-Powered
// Strip common AI preamble artifacts from recommendation text
// Matches ASCII control chars (excluding \t \n \r), DEL, C1 controls, soft
// hyphen, zero-width spaces/joiners/directional marks, line/paragraph
// separators, and BOM. Glitch residue from CSV/Excel imports of the rec
// library that otherwise renders as visible bars or mystery gaps.
const GLITCH_CHARS = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F\xAD\u200B-\u200F\u2028\u2029\uFEFF]/g;

const cleanAIText = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    // Strip leading AI preamble phrases
    .replace(/^(Here (are|is) (my|the|our) (recommendations?|analysis|suggestions?|assessment)[.:]\s*)/i, '')
    .replace(/^(Based on (the|my|our) (analysis|review|assessment)[,.:]\s*)/i, '')
    .replace(/^(I recommend (that )?(you |the company )?)/i, '')
    .replace(/^(Recommendation:\s*)/i, '')
    .replace(/^\*\*Recommendation\*\*:\s*/i, '')
    .replace(GLITCH_CHARS, ' ')
    .replace(/\s*\|\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const RecommendationsTab = ({ recommendations }) => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [hideAddressed, setHideAddressed] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');

  if (!recommendations) {
    return (
      <Card>
        <Card.Body className="text-center py-12">
          <TrendingUp aria-hidden="true" className="h-16 w-16 text-[#5a6a8a] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#e6f1ff] mb-2">No Recommendations Available</h3>
          <p className="text-[#a8b2d1]">Complete an assessment to receive personalized recommendations.</p>
        </Card.Body>
      </Card>
    );
  }

  // Handle both old and new recommendation formats
  const overallStatement = recommendations.overallStatement || {
    score: recommendations.overall_score,
    percentile: recommendations.overall_percentile,
    statement: recommendations.overall_statement
  };

  const recs = recommendations.recommendations || recommendations.category_recommendations || [];
  const summary = recommendations.summary || {};

  return (
    <div className="space-y-6">
      {/* Portfolio Summary - New Feature */}
      {summary.byType && (
        <Card>
          <Card.Header>
            <Card.Title icon={<Target />}>Recommendation Portfolio</Card.Title>
            <Card.Description>
              Balanced mix of quick wins, structural improvements, and strategic initiatives
            </Card.Description>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: 'Quick Win', label: 'Quick Wins', count: summary.byType.quickWins, desc: 'High impact, low effort', bg: 'bg-green-50', border: 'border-[rgba(0,255,136,0.25)]', text: 'text-green-600', sub: 'text-green-700' },
                { key: 'Structural', label: 'Structural Fixes', count: summary.byType.structuralFixes, desc: 'Medium-term improvements', bg: 'bg-blue-50', border: 'border-[rgba(0,217,255,0.25)]', text: 'text-blue-600', sub: 'text-blue-700' },
                { key: 'Strategic', label: 'Strategic Levers', count: summary.byType.strategicLevers, desc: 'Long-term transformation', bg: 'bg-purple-50', border: 'border-[rgba(168,85,247,0.25)]', text: 'text-purple-600', sub: 'text-purple-700' },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setTypeFilter(typeFilter === t.key ? 'all' : t.key)}
                  className={`text-center p-4 rounded-lg border transition-all cursor-pointer ${t.bg} ${typeFilter === t.key ? 'ring-2 ring-offset-1 ring-blue-400 ' + t.border : t.border + ' opacity-' + (typeFilter === 'all' ? '100' : '50')}`}
                >
                  <div className={`text-2xl font-bold ${t.text}`}>{t.count}</div>
                  <div className="text-sm font-medium text-[#e6f1ff]">{t.label}</div>
                  <div className={`text-xs ${t.sub}`}>{t.desc}</div>
                </button>
              ))}
            </div>
            {typeFilter !== 'all' && (
              <button onClick={() => setTypeFilter('all')} className="text-xs text-blue-400 hover:text-blue-300 mt-2">
                Show all types
              </button>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Individual Recommendations */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#e6f1ff] flex items-center gap-2">
            <Zap aria-hidden="true" className="w-5 h-5 text-yellow-500" />
            Prioritized Recommendations
          </h3>
          {recs.some(r => r.prism_status === 'addressed') && (
            <button
              onClick={() => setHideAddressed(!hideAddressed)}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border transition-colors"
              style={{
                background: hideAddressed ? '#f0f9ff' : '#f9fafb',
                borderColor: hideAddressed ? '#93c5fd' : '#e5e7eb',
                color: hideAddressed ? '#1d4ed8' : '#6b7280',
              }}
            >
              <EyeOff aria-hidden="true" className="w-3.5 h-3.5" />
              {hideAddressed ? 'Showing gaps only' : 'Hide addressed'}
            </button>
          )}
        </div>

        {recs.filter(r => {
          if (hideAddressed && r.prism_status === 'addressed') return false;
          if (typeFilter !== 'all' && r.type !== typeFilter) return false;
          return true;
        }).map((rec, index) => {
          const isExpanded = expandedCard === index;

          // Handle different recommendation formats (including RAG format)
          const rawTitle = rec.title || rec.recommendation || (rec.text ? rec.text.split('\n')[0] : 'Recommendation');
          const title = cleanAIText(rawTitle);
          const type = rec.type || 'Recommendation';
          const category = rec.category || rec.metadata?.category || 'General';
          const effort = rec.effort || 'Medium';
          const impact = rec.impact || 'Medium';
          const cost = rec.cost || '$$';
          const timeline = rec.timeline || rec.typical_timeline || '1-3 months';
          const reason = cleanAIText(rec.reason || rec.selection_reason || 'Selected for risk reduction');
          // actionSteps is normalized at write time (backend
          // import-recommendations.js parseActionSteps + 2026-04-10 DB
          // cleanup). Trust the array; fall back to empty if anything
          // upstream slips through.
          const actionSteps = Array.isArray(rec.actionSteps)
            ? rec.actionSteps
            : Array.isArray(rec.action_steps)
              ? rec.action_steps
              : [];

          let aiInsights = rec.aiInsights || rec.ai_insights;
          if (aiInsights && typeof aiInsights === 'object' && aiInsights.text) {
            aiInsights = aiInsights.text;
          }

          return (
            <Card key={index} className="hover:shadow-[0_8px_32px_rgba(0,217,255,0.12)] transition-shadow duration-200">
              <Card.Header>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge 
                        variant={type === 'Quick Win' ? 'success' : type === 'Strategic Lever' ? 'warning' : 'info'}
                        className="text-xs"
                      >
                        {type}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {category}
                      </Badge>
                      {rec.prism_status === 'addressed' && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded" style={{ background: '#dcfce7', color: '#166534' }}>
                          <CheckCircle aria-hidden="true" className="w-3 h-3" /> PRISM: Addressed
                        </span>
                      )}
                      {rec.prism_status === 'upgrade' && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded" style={{ background: '#fef3c7', color: '#92400e' }}>
                          <ArrowUpCircle className="w-3 h-3" /> PRISM: Upgrade Needed
                        </span>
                      )}
                      {rec.prism_status === 'confirmed_gap' && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded" style={{ background: '#fee2e2', color: '#991b1b' }}>
                          <XCircle aria-hidden="true" className="w-3 h-3" /> PRISM: Gap Confirmed
                        </span>
                      )}
                    </div>
                    <h4 className="font-semibold text-[#e6f1ff] text-lg leading-tight">
                      {title}
                    </h4>
                    {rec.prism_action_override && (
                      <div
                        className="mt-2 px-3 py-2 rounded text-xs"
                        style={{
                          background: 'rgba(168, 85, 247, 0.10)',
                          border: '1px solid rgba(168, 85, 247, 0.30)',
                          color: '#c4b5fd',
                        }}
                      >
                        <span style={{ fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: 10, marginRight: 8 }}>
                          ◆ PRISM suggests next:
                        </span>
                        {' '}
                        <span style={{ color: '#e6f1ff', fontStyle: 'italic' }}>{rec.prism_action_override}</span>
                      </div>
                    )}
                    {/* PRISM-grounded inline panel — coverage %, matched tools, named gaps,
                        before/after action steps. Renders only when engine attached
                        rec.prism_grounding (linked PRISM project + matched rec). */}
                    {rec.prism_grounding && (
                      <RecommendationGroundingPanel grounding={rec.prism_grounding} recTitle={title} />
                    )}
                    {/* Non-PRISM-grounded recs are culture / governance / process shape —
                        the rec doesn't map to a technical capability class so there's no
                        PRISM asset to ground against. Render a placeholder badge so the
                        absence reads as "different evidence stream coming" rather than
                        "nothing here". Two-evidence-stream architecture: Stream 1 (PRISM
                        technical, live) covers tech recs; Stream 2 (Foundations 360
                        culture, TBD) will cover these. Per EVIDENCE_GROUNDED_REC_
                        TRIANGULATION_PHASE_2_ALGORITHM_OPTIONS.md v2.1 §Two-evidence-
                        stream architecture. */}
                    {!rec.prism_grounding && (
                      <div
                        className="mt-2 px-3 py-2 rounded text-xs"
                        style={{
                          background: 'rgba(168, 85, 247, 0.05)',
                          border: '1px dashed rgba(168, 85, 247, 0.30)',
                          color: '#c4b5fd',
                        }}
                      >
                        <span style={{ fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: 10 }}>
                          ◆ Culture-grounded
                        </span>
                        <span style={{ color: '#8892b0', marginLeft: 8, fontSize: 11 }}>
                          coming in v2 — Foundations 360 scoring stream
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedCard(isExpanded ? null : index)}
                    className="ml-4 flex-shrink-0"
                  >
                    {isExpanded ? <ChevronDown aria-hidden="true" className="w-4 h-4" /> : <ChevronRight aria-hidden="true" className="w-4 h-4" />}
                  </Button>
                </div>
              </Card.Header>
              
              <Card.Body>
                {/* Key Metrics Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="text-center">
                    <div className={`text-sm font-medium ${effort === 'Low' ? 'text-green-600' : effort === 'High' ? 'text-red-600' : 'text-yellow-600'}`}>
                      {effort}
                    </div>
                    <div className="text-xs text-[#8892b0]">Effort</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-sm font-medium ${impact === 'High' ? 'text-green-600' : impact === 'Low' ? 'text-red-600' : 'text-yellow-600'}`}>
                      {impact}
                    </div>
                    <div className="text-xs text-[#8892b0]">Impact</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-blue-600">
                      {cost}
                    </div>
                    <div className="text-xs text-[#8892b0]">Cost</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-purple-600">
                      {timeline}
                    </div>
                    <div className="text-xs text-[#8892b0]">Timeline</div>
                  </div>
                </div>

                {/* Reason */}
                <div className="mb-4 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg">
                  <div className="text-sm text-[#ccd6f6]">
                    <strong>Why this matters:</strong> {reason}
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="space-y-4 pt-4 border-t border-[rgba(255,255,255,0.12)]">
                    {/* Action Steps — single list, best-available source:
                        PRISM-grounded v2 (with inline tool chips + facts) when
                        PRISM is linked and rec has grounding; original generic
                        steps otherwise. No Before/After framing — customer sees
                        ONE list, the most relevant one for their environment. */}
                    {(() => {
                      const groundedV2 = rec.prism_grounding?.grounded_action_steps_v2;
                      const hasV2 = Array.isArray(groundedV2) && groundedV2.length > 0;
                      const displaySteps = hasV2 ? groundedV2 : (actionSteps || []);
                      if (displaySteps.length === 0) return null;
                      return (
                        <div>
                          <h5 className="font-medium text-[#e6f1ff] mb-2 flex items-center gap-2">
                            <CheckCircle aria-hidden="true" className="w-4 h-4 text-green-500" />
                            Action Steps
                          </h5>
                          <ul className="space-y-2">
                            {displaySteps.map((step, stepIndex) => (
                              <li key={stepIndex} className="flex items-start gap-2">
                                <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
                                  {stepIndex + 1}
                                </span>
                                <span className="text-sm text-[#ccd6f6]">
                                  {hasV2 ? (
                                    <GroundedStepLine tokens={step.tokens} fallback={step.text} />
                                  ) : (
                                    step
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}

                    {/* AI Insights */}
                    {aiInsights && (
                      <div className="bg-blue-50 border border-[rgba(0,217,255,0.25)] rounded-lg p-4">
                        <h5 className="font-medium text-[#e6f1ff] mb-2 flex items-center gap-2">
                          <Activity aria-hidden="true" className="w-4 h-4" />
                          AI Implementation Guidance
                        </h5>
                        <p className="text-sm text-[#ccd6f6]">
                          {typeof aiInsights === 'string' ? aiInsights : 
                           (aiInsights?.text || aiInsights?.recommendation || 
                            JSON.stringify(aiInsights).substring(0, 200) + '...')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>

      {/* Footer */}
      <Card>
        <Card.Body className="bg-[rgba(255,255,255,0.03)]">
          <div className="text-center">
            <p className="text-sm text-[#a8b2d1] mb-2">
              🤖 Recommendations powered by Risk Aperture's AI and 1,220+ curated cybersecurity practices
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-[#8892b0]">
              <span>✨ Dynamic prioritization</span>
              <span>📊 Risk-based scoring</span>
              <span>🎯 Portfolio balanced</span>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

// Evidence Sync Dashboard (formerly Policy Integration Tab)
// Shows: uploaded documents, PRISM sync status, Iris coverage results, gap guidance
const PolicyIntegrationTab = ({ dashboardData }) => {
  const { sessionId } = useParams();
  const [historicalEvaluations, setHistoricalEvaluations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [guidanceData, setGuidanceData] = useState(null);
  const [loadingGuidance, setLoadingGuidance] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [prismEvidence, setPrismEvidence] = useState([]);
  const [prismEvidenceSummary, setPrismEvidenceSummary] = useState(null);

  useEffect(() => {
    loadHistoricalEvaluations();
    loadEvidenceGuidance();
    loadSyncStatus();
    loadPrismEvidence();
  }, [sessionId]);

  const loadHistoricalEvaluations = async () => {
    if (!sessionId) return;
    try {
      setLoadingHistory(true);
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/documents/evaluations/${sessionId}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setHistoricalEvaluations(data.evaluations || []);
      }
    } catch (error) {
      console.error('Error loading historical evaluations:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadEvidenceGuidance = async () => {
    if (!sessionId) return;
    try {
      setLoadingGuidance(true);
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/documents/evidence-guidance/${sessionId}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success && data.data) {
        setGuidanceData(data.data);
      }
    } catch (error) {
      console.error('Error loading evidence guidance:', error);
    } finally {
      setLoadingGuidance(false);
    }
  };

  const loadPrismEvidence = async () => {
    if (!sessionId) return;
    try {
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/documents/prism-evidence/${sessionId}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setPrismEvidence(data.evidence || []);
        setPrismEvidenceSummary(data.summary || null);
      }
    } catch (error) {
      console.error('Error loading PRISM evidence:', error);
    }
  };

  const loadSyncStatus = async () => {
    if (!sessionId) return;
    try {
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/documents/sync-status/${sessionId}`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) {
        setSyncStatus(data);
      }
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  const handleBulkSync = async () => {
    if (!sessionId) return;
    try {
      setSyncing(true);
      const response = await fetch(`${apiConfig.getApiBaseUrl()}/documents/sync-to-prism/${sessionId}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        loadSyncStatus();
        // Reload guidance after sync (give PRISM time to process)
        setTimeout(() => loadEvidenceGuidance(), 5000);
      }
    } catch (error) {
      console.error('Error syncing documents:', error);
    } finally {
      setSyncing(false);
    }
  };

  const getRiskColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getRiskBadgeVariant = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'warning';
    return 'danger';
  };

  const getPriorityColor = (priority) => {
    if (priority === 'critical') return { bg: 'rgba(255, 68, 68, 0.08)', border: 'rgba(255, 68, 68, 0.25)', text: 'var(--ra-danger)' };
    if (priority === 'high') return { bg: 'rgba(255, 165, 0, 0.08)', border: 'rgba(255, 165, 0, 0.25)', text: 'var(--ra-warning)' };
    if (priority === 'medium') return { bg: 'rgba(255, 165, 0, 0.06)', border: 'rgba(255, 165, 0, 0.18)', text: '#ffb833' };
    return { bg: 'rgba(0, 255, 136, 0.08)', border: 'rgba(0, 255, 136, 0.25)', text: 'var(--ra-success)' };
  };

  return (
    <div className="space-y-6">
      {/* Sync to PRISM — show when documents exist but no guidance yet */}
      {!guidanceData && historicalEvaluations.length > 0 && (
        <Card>
          <Card.Header>
            <Card.Title icon={<Shield />}>
              Evidence Intelligence <HelpTooltip contentKey="foundations.evidenceIntelligence" side="right" />
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="text-center py-6">
              <Shield aria-hidden="true" className="h-12 w-12 text-[#5a6a8a] mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-[#e6f1ff] mb-2">AI-Powered Evidence Analysis Available</h3>
              <p className="text-[#a8b2d1] mb-4 max-w-lg mx-auto">
                Sync your {historicalEvaluations.length} policy documents to PRISM for AI-powered compliance analysis.
                Get per-requirement coverage assessments and actionable guidance to close gaps.
              </p>
              {syncStatus && syncStatus.summary?.synced > 0 ? (
                <p className="text-sm text-green-600 mb-4">
                  {syncStatus.summary.synced} of {syncStatus.summary.total} documents synced — analysis in progress
                </p>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleBulkSync}
                  disabled={syncing}
                  icon={syncing ? <RefreshCw aria-hidden="true" className="w-4 h-4 animate-spin" /> : <Upload aria-hidden="true" className="w-4 h-4" />}
                >
                  {syncing ? 'Syncing...' : 'Sync Documents to PRISM'}
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* PRISM Evidence Intelligence — only when guidance data exists */}
      {guidanceData && guidanceData.coverage_summary && (
        <Card>
          <Card.Header>
            <Card.Title icon={<Shield />}>
              Evidence Intelligence <HelpTooltip contentKey="foundations.evidenceIntelligence" side="right" />
            </Card.Title>
            <Card.Description>
              PRISM AI analysis of your uploaded policies against compliance requirements
            </Card.Description>
            {/* Standard(s) attribution — names what the coverage % is measured against.
                Backed by guidanceData.coverage_summary.standards (PRISM
                /v2/evidence/integration-guidance). Defensive: array may be absent
                when running against an older PRISM deploy that pre-dates this surface. */}
            {Array.isArray(guidanceData.coverage_summary.standards) && guidanceData.coverage_summary.standards.length > 0 && (
              <div className="mt-2 text-sm text-[#a8b2d1]">
                Measured against{' '}
                <span className="font-semibold text-[#e6f1ff]">
                  {guidanceData.coverage_summary.standards.length <= 3
                    ? guidanceData.coverage_summary.standards.map(s => s.standard_name).join(', ')
                    : `${guidanceData.coverage_summary.standards.length} linked standards`}
                </span>
              </div>
            )}
          </Card.Header>
          <Card.Body>
            {/* Coverage Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--ra-cyber-aqua)' }}>
                  {guidanceData.coverage_summary.overall_coverage_pct}%
                </div>
                <div className="text-xs text-[#8892b0] mt-1">Control Coverage</div>
              </div>
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {guidanceData.coverage_summary.full_coverage}
                </div>
                <div className="text-xs text-[#8892b0] mt-1">Fully Covered</div>
              </div>
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {guidanceData.coverage_summary.partial_coverage}
                </div>
                <div className="text-xs text-[#8892b0] mt-1">Partially Covered</div>
              </div>
              <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">
                  {guidanceData.coverage_summary.no_coverage}
                </div>
                <div className="text-xs text-[#8892b0] mt-1">Not Covered</div>
              </div>
            </div>

            {/* Evidence Sync Status */}
            <div className="flex items-center gap-4 mb-6 p-3 rounded-lg" style={{ background: 'rgba(0, 255, 136, 0.08)', border: '1px solid rgba(0, 255, 136, 0.25)' }}>
              <CheckCircle aria-hidden="true" className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-[#ccd6f6]">
                {guidanceData.evidence_synced} documents synced to PRISM, {guidanceData.evidence_analyzed} analyzed by Iris
              </span>
            </div>

            {/* Gap Guidance Table */}
            {guidanceData.gap_guidance && guidanceData.gap_guidance.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-[#e6f1ff] mb-3">
                  Evidence Needed to Close Gaps
                </h3>
                <p className="text-sm text-[#8892b0] mb-4">
                  PRISM identified {guidanceData.gap_guidance.length} requirements where additional evidence would improve coverage
                </p>
                <div className="space-y-3">
                  {guidanceData.gap_guidance.slice(0, 10).map((gap, idx) => {
                    const colors = getPriorityColor(gap.priority);
                    const recs = gap.recommended_evidence || [];
                    return (
                      <div key={idx} className="border rounded-lg p-4" style={{ background: colors.bg, borderColor: colors.border }}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold" style={{ color: colors.text }}>
                                {gap.requirement_id}
                              </span>
                              <span className="text-sm text-[#ccd6f6]">{gap.requirement_name}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {gap.estimated_effort && (
                              <span className="text-xs text-[#8892b0] flex items-center gap-1">
                                <Clock aria-hidden="true" className="w-3 h-3" /> {gap.estimated_effort}
                              </span>
                            )}
                            <Badge variant={gap.priority === 'critical' ? 'danger' : gap.priority === 'high' ? 'warning' : 'default'}>
                              {gap.priority}
                            </Badge>
                          </div>
                        </div>
                        {gap.compliance_impact && (
                          <p className="text-xs text-[#a8b2d1] mb-2">{gap.compliance_impact}</p>
                        )}
                        {recs.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {recs.map((rec, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border"
                                style={{ background: 'var(--ra-glass-bg)', borderColor: 'var(--ra-glass-border)', color: '#ccd6f6' }}>
                                <FileText aria-hidden="true" className="w-3 h-3" />
                                {rec.title}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* CTA to PRISM */}
                <div className="mt-4 p-4 rounded-lg text-center" style={{ background: 'rgba(0, 217, 255, 0.08)', border: '1px solid rgba(0, 217, 255, 0.25)' }}>
                  <p className="text-sm" style={{ color: 'var(--ra-cyber-aqua)' }}>
                    View detailed evidence guidance, upload additional documents, and track compliance progress in PRISM
                  </p>
                  <button
                    className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: 'var(--ra-gradient-cta)', color: '#ffffff', border: 'none', cursor: 'pointer' }}
                    onClick={async () => {
                      try {
                        const res = await fetch(apiConfig.foundations.getDeepDive('EVIDENCE'), {
                          headers: authHeaders(),
                        });
                        const data = await res.json();
                        if (data.success && data.redirectUrl) {
                          window.open(data.redirectUrl, 'prism');
                        } else {
                          const prismBase = import.meta.env.VITE_PRISM_BASE_URL || 'http://localhost:3005';
                          window.open(prismBase, 'prism');
                        }
                      } catch {
                        const prismBase = import.meta.env.VITE_PRISM_BASE_URL || 'http://localhost:3005';
                        window.open(prismBase, 'prism');
                      }
                    }}
                  >
                    Open PRISM Evidence Library →
                  </button>
                </div>
              </div>
            )}

            {/* No gaps — everything covered */}
            {guidanceData.gap_guidance && guidanceData.gap_guidance.length === 0 && guidanceData.coverage_summary.total_requirements_evaluated > 0 && (
              <div className="text-center py-6">
                <CheckCircle aria-hidden="true" className="h-12 w-12 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-medium">All evaluated requirements have evidence coverage</p>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* PRISM Evidence Documents — from shared evidence registry */}
      {prismEvidence.length > 0 && (
        <Card>
          <Card.Header>
            <Card.Title icon={<Shield />}>Evidence Library</Card.Title>
            <Card.Description>
              Documents from both Foundations and PRISM assessments — {prismEvidenceSummary?.prism_documents || 0} from PRISM, {prismEvidenceSummary?.foundations_documents || 0} from Foundations
            </Card.Description>
          </Card.Header>
          <Card.Body>
            {prismEvidenceSummary && (
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-3 text-center">
                  <div className="text-xl font-bold" style={{ color: 'var(--ra-cyber-aqua)' }}>
                    {prismEvidence.length}
                  </div>
                  <div className="text-xs text-[#8892b0] mt-1">Total Documents</div>
                </div>
                <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-blue-400">
                    {prismEvidenceSummary.prism_documents}
                  </div>
                  <div className="text-xs text-[#8892b0] mt-1">From PRISM</div>
                </div>
                <div className="border border-[rgba(255,255,255,0.12)] rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-green-400">
                    {prismEvidenceSummary.total_requirements_covered}
                  </div>
                  <div className="text-xs text-[#8892b0] mt-1">Requirements Covered</div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgba(255,255,255,0.12)]">
                    <th className="text-left py-2 px-3 text-[#8892b0] font-medium">Source</th>
                    <th className="text-left py-2 px-3 text-[#8892b0] font-medium">Document</th>
                    <th className="text-left py-2 px-3 text-[#8892b0] font-medium">Type</th>
                    <th className="text-left py-2 px-3 text-[#8892b0] font-medium">AI Classification</th>
                    <th className="text-left py-2 px-3 text-[#8892b0] font-medium">Controls</th>
                  </tr>
                </thead>
                <tbody>
                  {prismEvidence.map((ev) => (
                    <tr key={`${ev.source_product}-${ev.source_id}`} className="border-b border-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.03)]">
                      <td className="py-2 px-3">
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded"
                          style={{
                            background: ev.source_product === 'prism' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                            color: ev.source_product === 'prism' ? '#60a5fa' : '#4ade80',
                            border: `1px solid ${ev.source_product === 'prism' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
                          }}
                        >
                          {ev.source_product === 'prism' ? 'PRISM' : 'Foundations'}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-[#ccd6f6]">
                        {ev.document_name}
                        {ev.ai_summary && (
                          <div className="text-xs text-[#8892b0] mt-0.5 truncate" style={{ maxWidth: 300 }}>
                            {ev.ai_summary}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-[#8892b0] capitalize">{ev.document_type || '—'}</td>
                      <td className="py-2 px-3 text-[#8892b0] capitalize">{ev.ai_classification || '—'}</td>
                      <td className="py-2 px-3">
                        {ev.compliance_controls && ev.compliance_controls.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {ev.compliance_controls.slice(0, 3).map((ctrl, i) => (
                              <span key={i} className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.08)', color: '#8892b0' }}>
                                {ctrl}
                              </span>
                            ))}
                            {ev.compliance_controls.length > 3 && (
                              <span className="text-xs text-[#8892b0]">+{ev.compliance_controls.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[#8892b0]">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Historical Evaluations Section */}
      <Card>
        <Card.Header>
          <Card.Title icon={<Shield />}>Policy Evaluations from C-Level Tech Assessment</Card.Title>
          <Card.Description>
            Documents evaluated during the cybersecurity assessment
          </Card.Description>
        </Card.Header>
        <Card.Body>
          {loadingHistory ? (
            <div className="text-center py-8">
              <Clock aria-hidden="true" className="h-12 w-12 text-[#5a6a8a] mx-auto mb-2 animate-pulse" />
              <p className="text-[#a8b2d1]">Loading historical evaluations...</p>
            </div>
          ) : historicalEvaluations.length > 0 ? (
            <div className="space-y-4">
              {historicalEvaluations.map((evaluation, index) => (
                <div key={evaluation.id || index} className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText aria-hidden="true" className="w-5 h-5 text-blue-600" />
                        <h4 className="font-medium text-[#e6f1ff]">{evaluation.documentName}</h4>
                      </div>
                      <p className="text-sm text-[#a8b2d1] mb-1">Policy Type: {evaluation.policyType}</p>
                      <p className="text-sm text-[#8892b0]">
                        Evaluated: {new Date(evaluation.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${getRiskColor(evaluation.overallScore)}`}>
                        {evaluation.overallScore}%
                      </div>
                      <Badge variant={getRiskBadgeVariant(evaluation.overallScore)}>
                        {evaluation.overallScore >= 80 ? 'Compliant' :
                         evaluation.overallScore >= 60 ? 'Moderate' :
                         evaluation.overallScore >= 40 ? 'Needs Work' : 'Non-Compliant'}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText aria-hidden="true" className="h-12 w-12 text-[#5a6a8a] mx-auto mb-2" />
              <p className="text-[#a8b2d1]">No policy documents were evaluated during the assessment</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Persona Divergence Modal removed — renders inline */}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PRISM Drift Banner — action-required alert shown above the tab row when
// PRISM continuous monitoring detects open drift events. SPRS + ATO Journey
// (formerly in the same band) now render inside the PRISM Posture card.
// ─────────────────────────────────────────────────────────────────────────────
const PrismDriftBanner = ({ drift }) => {
  if (!drift || drift.open_count <= 0) return null;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2">
        <div
          className={`rounded-lg border p-4 mb-3 flex items-start gap-3 ${
            drift.max_open_severity === 'critical' ? 'bg-red-500/10 border-red-500/40' :
            drift.max_open_severity === 'high'     ? 'bg-orange-500/10 border-orange-500/40' :
            drift.max_open_severity === 'medium'   ? 'bg-amber-500/10 border-amber-500/40' :
                                                      'bg-cyan-500/10 border-cyan-500/30'
          }`}
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-300" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#e6f1ff]">
              {drift.open_count} open compliance drift{drift.open_count === 1 ? '' : 's'}
              {drift.critical_open > 0 && ` — ${drift.critical_open} critical`}
              {drift.high_open > 0 && ` — ${drift.high_open} high`}
            </div>
            <div className="text-xs text-[#a8b2d1] mt-0.5">
              {drift.baseline_taken_at
                ? `Since baseline ${new Date(drift.baseline_taken_at).toLocaleDateString()}`
                : 'Drift detected'}
              {drift.latest_detected_at
                ? ` · latest ${new Date(drift.latest_detected_at).toLocaleString()}`
                : ''}
            </div>
          </div>
        </div>
    </div>
  );
};

export default AssessmentDashboard;